import React, { useLayoutEffect, useMemo } from "react";
import { useTheme } from "@material-ui/core";
import clsx from "clsx";
import { useDeviceTypes } from "halifax";
import { RouteComponentProps } from "react-router-dom";
import { FlightModuleProps, ExperimentState } from "redmond";
import { useUserContext, isCorpTenant, CDN_ASSETS } from "@capone/common";

import ItineraryReviewBackground from "../../assets/b2b/itinerary-review-background.jpeg";
import useMicrofrontend from "../../hooks/useMicrofrontend";
import config from "../../utils/config";
import { colors as caponeColors } from "../../utils/capone/colors";
import { colors as corpColors } from "../../utils/capone-corporate/colors";
import { PAWTUCKET_FLIGHTS_MODULE_ID } from "../../utils/moduleIds";
import "./styles.scss";

interface IFlightsModuleProps extends RouteComponentProps {
  language: string;
  experiments?: ExperimentState;
  crossSellLoaded: boolean;
  recentlyViewedLoaded: boolean;
  annualTravelCreditLoaded: boolean;
  showCoreHomepage?: boolean;
}

const FlightsModule = (props: IFlightsModuleProps) => {
  const {
    language,
    experiments,
    crossSellLoaded,
    recentlyViewedLoaded,
    annualTravelCreditLoaded,
    showCoreHomepage,
    ...routerProps
  } = props;
  const { history } = routerProps;
  const id = PAWTUCKET_FLIGHTS_MODULE_ID;
  const theme = useTheme();
  const { matchesDesktop, matchesSmallDesktop } = useDeviceTypes();
  const { isLoaded, ptFlightsModule } = useMicrofrontend(
    id,
    (config.ptFlightsModuleUrl += "")
  );
  const { sessionInfo, policies, logo, isRewardsDisabled } = useUserContext(
    config.TENANT
  );

  const isCorp = isCorpTenant(config.TENANT);
  const moduleProps: FlightModuleProps = useMemo(
    () => ({
      colors: isCorp ? corpColors : caponeColors,
      experiments,
      language,
      theme,
      baseHistory: history,
      clientAssets: {
        sessionInfo,
        logo: <img src={logo?.src} alt={logo?.alt} />,
        itineraryReviewBackground: ItineraryReviewBackground,
        policies,
        isRewardsDisabled,
      },
      isAgentPortal: config.ptIsAgentPortal,
      searchImage: isCorp
        ? CDN_ASSETS.caponeCorporate.searchLoading.flights
        : CDN_ASSETS.capone.searchLoading.flights,
      showCoreHomepage,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isRewardsDisabled]
  );

  const isCrossSell: boolean = useMemo(
    () => crossSellLoaded,
    [crossSellLoaded]
  );

  const isRecentlyViewed: boolean = useMemo(
    () => recentlyViewedLoaded,
    [recentlyViewedLoaded]
  );

  useLayoutEffect(() => {
    if (!ptFlightsModule) return;
    const { render, unMount } = ptFlightsModule;

    render(id, moduleProps);
    return () => {
      unMount(id);
    };
  }, [isLoaded, moduleProps]); // eslint-disable-line

  if (!isLoaded) return null;
  if (!ptFlightsModule) return <h2>ERROR! Cannot find Module</h2>;
  return (
    <div
      className={clsx("flight-module-container", {
        desktop: matchesDesktop || matchesSmallDesktop,
        "cross-sell": isCrossSell,
        "recently-viewed": isRecentlyViewed,
        "annual-travel-credit": annualTravelCreditLoaded,
      })}
      id={id}
    />
  );
};

export default FlightsModule;
